import React from "react";

import { Icon, IconSize } from "@gaming/xds-react-js";
import { ChevronRight } from "@gaming/xds-react-js-icons";

import { GetVerifiableCredentials, partnerSectionLinks } from "../../constants/urls";
import { legacyCategorySubjectIds } from "../../constants/supportSubjects";
import { FeatureFlags, useFeatureFlag } from "../../featureFlags";
import { AssistantEscalation, EscalationTarget } from "../../helpers/supportAssistantStore";
import { supportNewCasePath, supportPagePath } from "../../helpers/supportCases";
import { useStrings } from "../../localization/localizationHelpers";
import { CopyButton } from "./CopyButton";
import { AssistantLink } from "./renderAssistantMarkdown";
import * as styles from "./SupportAssistant.module.css";

const labelKeys: Readonly<Record<EscalationTarget, string>> = {
  supportTicket: "supportAssistant_escalationSupportTicket",
  devkitSetup: "supportAssistant_escalationDevkitSetup",
  accountVerification: "supportAssistant_escalationAccountVerification",
};

type EscalationCardProps = Readonly<{ escalation: AssistantEscalation }>;

export const EscalationCard: React.FC<EscalationCardProps> = ({ escalation }) => {
  const isCasesEnabled = useFeatureFlag(FeatureFlags.EnableHubSupportCases);
  const [label, summaryLabel] = useStrings(
    labelKeys[escalation.target] ?? labelKeys.supportTicket,
    "supportAssistant_escalationSummary"
  );
  const ticket =
    escalation.target === "supportTicket" && isCasesEnabled ? escalation.ticket : undefined;
  const subjectId = ticket?.category ? legacyCategorySubjectIds[ticket.category] : undefined;
  const query =
    ticket &&
    new URLSearchParams({
      title: ticket.title,
      severity: ticket.severity,
      description: ticket.description,
      ...(subjectId ? { subjectId } : {}),
      ...(ticket.gameConceptId ? { gameConceptId: ticket.gameConceptId } : {}),
    });
  const href =
    escalation.target === "devkitSetup"
      ? partnerSectionLinks.developmentHardware.path
      : escalation.target === "accountVerification"
      ? GetVerifiableCredentials
      : isCasesEnabled
      ? `${supportNewCasePath}${query ? `?${query}` : ""}`
      : supportPagePath;

  return (
    <div className={styles.escalation} data-testid="support-assistant-escalation">
      <AssistantLink href={href} className={styles.card}>
        <span className={styles.cardTitle}>{label}</span>
        <Icon aria-hidden="true" icon={ChevronRight} iconSize={IconSize.Custom} />
      </AssistantLink>
      {escalation.summary && (
        <div className={styles.escalationSummary}>
          <p>{escalation.summary}</p>
          <CopyButton label={summaryLabel} text={escalation.summary} />
        </div>
      )}
    </div>
  );
};
